<script>
import { useVuelidate } from '@vuelidate/core';
import { or, requiredIf, url } from '@vuelidate/validators';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import ButtonNext from 'next/button/Button.vue';
import IconNext from 'next/icon/Icon.vue';
import VariablesDropdown from '../variablesDropdown/Index.vue';
import variablesMixins from '../variablesDropdown/variablesMixins';
import { containsVariable } from './helpers/validations';

export default {
  components: {
    VariablesDropdown,
    ButtonNext,
    IconNext,
  },
  mixins: [variablesMixins],
  props: {
    value: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    defaultValue: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    label: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    fallbackLabel: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      v$: useVuelidate(),
      toLocale,
    };
  },
  data() {
    return {
      updatedValue: this.value ?? this.defaultValue,
    };
  },
  computed: {
    descriptionTooltip() {
      if (!this.description) return null;

      return {
        content: this.toLocale(this.description),
        popperClass:
          'max-w-[300px] max-h-[300px] overflow-y-auto whitespace-pre-line',
        boundary: 'body',
        popperTriggers: ['hover'],
      };
    },
  },
  validations() {
    return {
      updatedValue: {
        required: requiredIf(this.required),
        url: or(url, containsVariable),
      },
    };
  },
  watch: {
    value(value) {
      this.updatedValue = value;
    },
  },
  mounted() {
    if (this.dirty) {
      this.$nextTick(() => {
        this.v$.$validate();
      });
    }
  },
  methods: {
    updateValue() {
      this.v$.$touch();
      this.$emit('change', this.updatedValue);
    },
    onVariableAdd(updatedValue) {
      this.updatedValue = updatedValue;
      this.updateValue();
    },
  },
};
</script>

<template>
  <div>
    <div
      class="relative flex flex-col min-w-0 gap-1"
      :class="{ error: v$.updatedValue.$error }"
    >
      <label
        v-if="label || descriptionTooltip"
        class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
      >
        <div v-if="label" class="block truncate">
          {{ toLocale(label, fallbackLabel) }}<span v-if="required">*</span>
        </div>
        <IconNext
          v-if="descriptionTooltip"
          v-tooltip.top="descriptionTooltip"
          icon="i-lucide-info"
          class="text-n-slate-11 size-3.5"
        />
      </label>

      <div class="flex relative">
        <!-- eslint-disable -->
        <input
          ref="fieldEl"
          v-model.trim="updatedValue"
          type="url"
          class="input peer block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
          :class="{ 'focus:!pe-10': showVariablesSelector }"
          :placeholder="toLocale(placeholder)"
          :disabled="disabled"
          @change="updateValue"
          @blur="v$.updatedValue.$touch"
          @keyup="openVariablesDropdown"
        />
        <!--eslint-enable-->

        <ButtonNext
          v-if="showVariablesSelector"
          class="absolute right-0 top-0 m-2 invisible peer-focus:visible active:visible focus:visible"
          icon="i-lucide-braces"
          variant="faded"
          color="slate"
          size="xs"
          @click="openVariablesDropdown"
        />
      </div>

      <p
        v-if="v$.updatedValue.$error"
        class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
      >
        <template v-if="v$.updatedValue.required.$invalid">
          {{ $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.REQUIRED') }}
        </template>
        <template v-else-if="v$.updatedValue.url.$invalid">
          {{ $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.URL') }}
        </template>
        <template v-else>
          {{ $t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.INVALID') }}
        </template>
      </p>
    </div>

    <VariablesDropdown
      :show-dropdown="showVariablesDropdown"
      :event="variablesDropdownEvent"
      :variables="variables"
      :filter-by-prefix="variablesFilter"
      @close="closeVariablesDropdown"
      @add="addVariable"
    />
  </div>
</template>

<style lang="scss" scoped>
input {
  @apply border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand;
}

.error input {
  @apply border-n-ruby-8 dark:border-n-ruby-8 hover:border-n-ruby-9 dark:hover:border-n-ruby-9 disabled:border-n-ruby-8 dark:disabled:border-n-ruby-8;
}
</style>
